import React, { useEffect, useState, useCallback } from 'react';
import { apiService } from '@/services/api';
import { liveSongRequestWebSocketService } from '@/services/liveSongRequestWebSocket';
import styles from './LiveSongRequestPanel.module.scss';

interface LiveSongRequest {
  id: string;
  status: string;
  bookingId?: string;
  songDescription?: string;
  sourceLink?: string;
  customerComments?: string;
  fileUrl?: string;
  photoUrl?: string;
  commentsEnabled?: boolean;
  displayName?: string;
  profilePic?: string;
  isOrganizer?: boolean;
  is_organizer?: boolean;
  organizationName?: string;
  organization_name?: string;
  organizerLogoUrl?: string;
  organizer_logo_url?: string;
  [key: string]: any;
}

interface ArrivedBooking {
  id: string;
  tableName?: string;
}

interface Comment {
  id: string;
  commentText: string;
  createdAt?: string;
  imageUrls?: string[];
  [key: string]: any;
}

function unwrapData<T>(res: { data?: T } | T): T | undefined {
  if (res == null) return undefined;
  if (typeof res === 'object' && 'data' in res && res.data !== undefined) return res.data as T;
  return res as T;
}

const REQUESTS_PAGE_LIMIT = 20;
const COMMENT_IMAGES_MAX = 10;
const COMMENT_IMAGE_MAX_SIZE_BYTES = 50 * 1024 * 1024; // 50MB (images and videos)

interface LiveSongRequestListProps {
  branchId: string;
  /** Called when user clicks "Request a song" (e.g. open modal). */
  onRequestSongClick?: () => void;
  /** Optional extra class for the section. */
  className?: string;
}

export default function LiveSongRequestList({ branchId, onRequestSongClick, className }: LiveSongRequestListProps) {
  const [available, setAvailable] = useState<boolean>(false);
  const [commentsEnabled, setCommentsEnabled] = useState<boolean>(false);
  const [requests, setRequests] = useState<LiveSongRequest[]>([]);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [loadingAvailable, setLoadingAvailable] = useState<boolean>(true);
  const [loadingRequests, setLoadingRequests] = useState<boolean>(false);
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);
  const [comments, setComments] = useState<Record<string, Comment[]>>({});
  const [loadingComments, setLoadingComments] = useState<Record<string, boolean>>({});
  const [newCommentText, setNewCommentText] = useState<Record<string, string>>({});
  const [postingComment, setPostingComment] = useState<Record<string, boolean>>({});
  const [requestAgainLoading, setRequestAgainLoading] = useState<Record<string, boolean>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [currentRequest, setCurrentRequest] = useState<LiveSongRequest | null>(null);
  const [loadingCurrent, setLoadingCurrent] = useState<boolean>(false);
  const [currentCommentText, setCurrentCommentText] = useState<string>('');
  const [currentCommentImages, setCurrentCommentImages] = useState<File[]>([]);
  const [currentCommentImagePreviewUrls, setCurrentCommentImagePreviewUrls] = useState<string[]>([]);
  const currentCommentImagesInputRef = React.useRef<HTMLInputElement>(null);
  const [postingCurrentComment, setPostingCurrentComment] = useState<boolean>(false);
  const [commentSentForReview, setCommentSentForReview] = useState<boolean>(false);
  const [currentCommentImageError, setCurrentCommentImageError] = useState<string | null>(null);
  const [arrivedBookings, setArrivedBookings] = useState<ArrivedBooking[]>([]);
  const [showSlideshowPreview, setShowSlideshowPreview] = useState<boolean>(false);
  const [slideshowPreviewIndex, setSlideshowPreviewIndex] = useState<number>(0);

  const fetchAvailable = useCallback(async () => {
    if (!branchId) return;
    setLoadingAvailable(true);
    try {
      const [availRes, seatsRes] = await Promise.all([
        apiService.getLiveSongAvailable(branchId),
        apiService.getMyBookingSeats().catch(() => null),
      ]);
      const data = unwrapData<{ available?: boolean; commentsEnabled?: boolean; arrivedBookings?: ArrivedBooking[] }>(availRes);
      setCommentsEnabled(Boolean(data?.commentsEnabled));
      const bookings = Array.isArray(data?.arrivedBookings) ? data.arrivedBookings : [];

      const seatsData = seatsRes ? unwrapData<{ seats?: any[] }>(seatsRes) : null;
      const seats = Array.isArray(seatsData?.seats) ? seatsData.seats : [];
      const chairBookings: ArrivedBooking[] = seats
        .filter((s: any) => {
          if ((s.status || '').toLowerCase() !== 'approved') return false;
          const b = s.booking;
          return b && b.branchId === branchId && ['active', 'confirmed'].includes((b.status || '').toLowerCase());
        })
        .map((s: any) => ({
          id: s.bookingId || s.booking?.id,
          tableName: s.booking?.table?.tableName || s.booking?.table?.table_name || `Table ${s.booking?.table?.tableNumber ?? ''}`.trim(),
        }));

      const merged = [...bookings];
      for (const cb of chairBookings) {
        if (cb.id && !merged.some((b) => b.id === cb.id)) {
          merged.push(cb);
        }
      }

      setAvailable(Boolean(data?.available) || merged.length > 0);
      setArrivedBookings(merged);
    } catch {
      setAvailable(false);
      setCommentsEnabled(false);
      setArrivedBookings([]);
    } finally {
      setLoadingAvailable(false);
    }
  }, [branchId]);

  const fetchRequests = useCallback(async (pageNum: number = page) => {
    if (!branchId) return;
    setLoadingRequests(true);
    try {
      const res = await apiService.getLiveSongRequests(branchId, {
        page: pageNum,
        limit: REQUESTS_PAGE_LIMIT
      });
      const data = unwrapData<{ requests?: any[]; total?: number } | any[]>(res);
      const list = Array.isArray(data) ? data : (Array.isArray((data as any)?.requests) ? (data as any).requests : []);
      setRequests(list);
      if (data && typeof data === 'object' && 'total' in data && typeof (data as any).total === 'number') {
        setTotal((data as any).total);
      }
    } catch {
      setRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  }, [branchId, page]);

  const fetchComments = useCallback(async (requestId: string) => {
    setLoadingComments((prev) => ({ ...prev, [requestId]: true }));
    try {
      const res = await apiService.getLiveSongRequestComments(requestId);
      const data = unwrapData<{ comments?: any[] }>(res);
      setComments((prev) => ({ ...prev, [requestId]: Array.isArray(data?.comments) ? data.comments : [] }));
    } catch {
      setComments((prev) => ({ ...prev, [requestId]: [] }));
    } finally {
      setLoadingComments((prev) => ({ ...prev, [requestId]: false }));
    }
  }, []);

  const fetchCurrentRequest = useCallback(async () => {
    if (!branchId) return;
    setLoadingCurrent(true);
    try {
      const res = await apiService.getLiveSongCurrentRequest(branchId);
      const raw = unwrapData<{ request?: LiveSongRequest | null } | LiveSongRequest | null>(res);
      const data = raw && typeof raw === 'object' && 'request' in raw ? (raw as { request?: LiveSongRequest | null }).request : raw;
      const req = data && typeof data === 'object' && 'id' in data && (data as LiveSongRequest).id
        ? (data as LiveSongRequest)
        : null;
      setCurrentRequest(req);
      if (req?.id) fetchComments(req.id);
    } catch {
      setCurrentRequest(null);
    } finally {
      setLoadingCurrent(false);
    }
  }, [branchId, fetchComments]);

  useEffect(() => {
    fetchAvailable();
  }, [fetchAvailable]);

  useEffect(() => {
    if (!available || !branchId) return;
    fetchRequests(page);
    fetchCurrentRequest();
    liveSongRequestWebSocketService.connect(branchId);

    const onEvent = () => {
      fetchRequests(page);
      fetchAvailable();
      fetchCurrentRequest();
    };

    const onReconnect = () => {
      fetchRequests(page);
      fetchAvailable();
      fetchCurrentRequest();
    };

    const onCommentNew = (payload: any) => {
      const reqId = payload?.requestId ?? payload?.liveSongRequestId;
      if (reqId) fetchComments(reqId);
      fetchRequests(page);
      fetchCurrentRequest();
    };

    liveSongRequestWebSocketService.on('connect', onReconnect);
    liveSongRequestWebSocketService.on('live_song:config_updated', onEvent);
    liveSongRequestWebSocketService.on('live_song_request:created', onEvent);
    liveSongRequestWebSocketService.on('live_song_request:scheduled', onEvent);
    liveSongRequestWebSocketService.on('live_song_request:rejected', onEvent);
    liveSongRequestWebSocketService.on('live_song_request:current', onEvent);
    liveSongRequestWebSocketService.on('live_song_request:closed', onEvent);
    liveSongRequestWebSocketService.on('live_song_request_comment:new', onCommentNew);

    return () => {
      liveSongRequestWebSocketService.off('connect', onReconnect);
      liveSongRequestWebSocketService.off('live_song:config_updated', onEvent);
      liveSongRequestWebSocketService.off('live_song_request:created', onEvent);
      liveSongRequestWebSocketService.off('live_song_request:scheduled', onEvent);
      liveSongRequestWebSocketService.off('live_song_request:rejected', onEvent);
      liveSongRequestWebSocketService.off('live_song_request:current', onEvent);
      liveSongRequestWebSocketService.off('live_song_request:closed', onEvent);
      liveSongRequestWebSocketService.off('live_song_request_comment:new', onCommentNew);
      liveSongRequestWebSocketService.disconnect();
    };
  }, [available, branchId, page, fetchRequests, fetchAvailable, fetchComments, fetchCurrentRequest]);

  const handleExpandRequest = (requestId: string) => {
    if (expandedRequestId === requestId) {
      setExpandedRequestId(null);
      return;
    }
    setExpandedRequestId(requestId);
    if (!comments[requestId]) fetchComments(requestId);
  };

  const handleCurrentCommentImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    setCurrentCommentImageError(null);
    const oversized = newFiles.filter((f) => f.size > COMMENT_IMAGE_MAX_SIZE_BYTES);
    if (oversized.length > 0) {
      setCurrentCommentImageError(`Each file must be under 50MB. ${oversized.length} file(s) too large.`);
      return;
    }
    const invalid = newFiles.filter((f) => !f.type.startsWith('image/') && !f.type.startsWith('video/'));
    if (invalid.length > 0) {
      setCurrentCommentImageError('Only image and video files are allowed.');
      return;
    }
    setCurrentCommentImages((prev) => {
      const combined = [...prev, ...newFiles].slice(0, COMMENT_IMAGES_MAX);
      if (combined.length > COMMENT_IMAGES_MAX) {
        setCurrentCommentImageError(`Max ${COMMENT_IMAGES_MAX} files allowed. Only the first ${COMMENT_IMAGES_MAX} were added.`);
      }
      return combined;
    });
    if (currentCommentImagesInputRef.current) currentCommentImagesInputRef.current.value = '';
  };

  const handleRemoveCommentImage = (index: number) => {
    setCurrentCommentImages((prev) => prev.filter((_, i) => i !== index));
    setCurrentCommentImageError(null);
  };

  useEffect(() => {
    setCurrentCommentImagePreviewUrls((prev) => {
      prev.forEach((u) => URL.revokeObjectURL(u));
      return currentCommentImages.map((f) => URL.createObjectURL(f));
    });
  }, [currentCommentImages]);

  useEffect(() => {
    return () => currentCommentImagePreviewUrls.forEach((u) => URL.revokeObjectURL(u));
  }, [currentCommentImagePreviewUrls]);

  useEffect(() => {
    if (!showSlideshowPreview) return;
    const onEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowSlideshowPreview(false); };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [showSlideshowPreview]);

  const handleAddCurrentComment = async () => {
    const text = currentCommentText.trim();
    const hasText = text.length > 0;
    const hasImages = currentCommentImages.length > 0;
    if (!currentRequest?.id || (!hasText && !hasImages)) return;
    setPostingCurrentComment(true);
    setCommentSentForReview(false);
    setCurrentCommentImageError(null);
    try {
      await apiService.addLiveSongRequestComment(currentRequest.id, {
        ...(hasText && { commentText: text }),
        ...(hasImages && { commentImages: currentCommentImages })
      });
      setCurrentCommentText('');
      setCurrentCommentImages([]);
      if (currentCommentImagesInputRef.current) currentCommentImagesInputRef.current.value = '';
      setCommentSentForReview(true);
      setTimeout(() => setCommentSentForReview(false), 4000);
      fetchComments(currentRequest.id);
    } catch (err: any) {
      const msg = err?.message ?? 'Failed to send.';
      setCurrentCommentImageError(msg);
    } finally {
      setPostingCurrentComment(false);
    }
  };

  const handleRequestAgain = async (req: LiveSongRequest) => {
    if (!branchId || !req.songDescription?.trim()) return;
    const bookingId = req.bookingId || '';
    if (!bookingId) return;
    setRequestAgainLoading((prev) => ({ ...prev, [req.id]: true }));
    try {
      await apiService.createLiveSongRequest(branchId, {
        bookingId,
        songDescription: (req.songDescription || '').trim(),
        ...(req.sourceLink?.trim() && { sourceLink: req.sourceLink.trim() }),
        ...(req.customerComments?.trim() && { customerComments: req.customerComments.trim() }),
        ...(req.fileUrl?.trim() && { fileUrl: req.fileUrl.trim() }),
        ...(req.photoUrl?.trim() && { photoUrl: req.photoUrl.trim() })
      });
      fetchRequests(page);
      fetchAvailable();
      setSuccessMessage('Song request is successful');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch {
      // leave loading false; user can retry
    } finally {
      setRequestAgainLoading((prev) => ({ ...prev, [req.id]: false }));
    }
  };

  if (loadingAvailable || !available) return null;

  return (
    <section className={`${styles.panel} ${styles.listOnPage} ${className || ''}`} aria-labelledby="live-song-list-heading">
      <div className={styles.listSectionHeader}>
        <h2 id="live-song-list-heading" className={styles.heading}>
          My song requests
        </h2>
        {onRequestSongClick && (
          <button type="button" className={styles.submitBtn} onClick={onRequestSongClick}>
            Request a song
          </button>
        )}
      </div>
      {successMessage && (
        <p className={styles.successMessage} role="status">
          {successMessage}
        </p>
      )}
      {commentsEnabled && (
        <div className={styles.nowPlayingSection}>
          <h3 className={styles.nowPlayingTitle}>Now playing</h3>
          {loadingCurrent ? (
            <p className={styles.muted}>Loading…</p>
          ) : currentRequest ? (
            <>
              <p className={styles.nowPlayingSong}>{currentRequest.songDescription || 'Song request'}</p>
              {currentRequest.customerComments && (
                <p className={styles.nowPlayingDedication}>{currentRequest.customerComments}</p>
              )}
              {(() => {
                const isOrg = currentRequest.isOrganizer ?? (currentRequest as any).is_organizer;
                const orgName = currentRequest.organizationName ?? (currentRequest as any).organization_name;
                const orgLogo = currentRequest.organizerLogoUrl ?? (currentRequest as any).organizer_logo_url;
                const custName = currentRequest.displayName ?? (currentRequest as any).booking?.customer?.displayName;
                const custPic = currentRequest.profilePic ?? (currentRequest as any).booking?.customer?.profilePic;
                const showRequestedBy = isOrg ? orgName || orgLogo : custName || custPic || (currentRequest as any).booking?.customer;
                if (!showRequestedBy) return null;
                const displayName = isOrg ? (orgName || 'Organizer') : (custName || 'Customer');
                const avatarUrl = isOrg ? orgLogo : custPic;
                return (
                  <div className={styles.nowPlayingRequestedBy}>
                    <span className={styles.nowPlayingRequestedByLabel}>Requested by</span>
                    <div className={styles.nowPlayingRequestedByUser}>
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="" className={styles.nowPlayingAvatar} />
                      ) : (
                        <div className={styles.nowPlayingAvatarPlaceholder}>
                          {(displayName.charAt(0) || '?').toUpperCase()}
                        </div>
                      )}
                      <span className={styles.nowPlayingDisplayName}>{displayName}</span>
                    </div>
                  </div>
                );
              })()}
              <div className={styles.commentsBlock}>
                <h4 className={styles.commentsTitle}>Comments</h4>
                {loadingComments[currentRequest.id] ? (
                  <p className={styles.muted}>Loading comments…</p>
                ) : (
                  <>
                    <ul className={styles.commentList}>
                      {(comments[currentRequest.id] ?? []).map((c) => (
                        <li key={c.id} className={styles.commentItem}>
                          {c.commentText && <span className={styles.commentText}>{c.commentText}</span>}
                          {c.imageUrls && c.imageUrls.length > 0 && (
                            <div className={styles.commentImages}>
                              {c.imageUrls.map((url, i) => {
                                const isVideoUrl = /\.(mp4|webm|mov)(\?|$)/i.test(url);
                                return (
                                  <a key={i} href={url} target="_blank" rel="noopener noreferrer" className={styles.commentImageLink}>
                                    {isVideoUrl ? (
                                      <video src={url} className={styles.commentImageThumb} muted playsInline preload="metadata" />
                                    ) : (
                                      <img src={url} alt="" className={styles.commentImageThumb} />
                                    )}
                                  </a>
                                );
                              })}
                            </div>
                          )}
                          {c.createdAt && (
                            <span className={styles.commentTime}>{new Date(c.createdAt).toLocaleString()}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                    {(() => {
                      const isRequestOwner = currentRequest.bookingId && arrivedBookings.some((b) => b.id === currentRequest.bookingId);
                      const hasInput = currentCommentText.trim() || currentCommentImages.length > 0;
                      return (
                        <>
                          <div className={styles.addCommentRow}>
                            <input
                              type="text"
                              className={styles.commentInput}
                              placeholder={isRequestOwner ? 'Add a comment and/or images/videos for slideshow…' : 'Add a comment… (sent for organizer review)'}
                              value={currentCommentText}
                              onChange={(e) => setCurrentCommentText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddCurrentComment();
                                }
                              }}
                            />
                            <button
                              type="button"
                              className={styles.commentBtn}
                              onClick={handleAddCurrentComment}
                              disabled={postingCurrentComment || !hasInput}
                            >
                              {postingCurrentComment ? 'Sending…' : 'Send'}
                            </button>
                          </div>
                          {isRequestOwner && (
                            <div className={styles.commentImagesUpload}>
                              <label className={styles.commentImagesAddFilesLabel}>
                                <input
                                  ref={currentCommentImagesInputRef}
                                  type="file"
                                  accept="image/*,video/*,audio/*"
                                  multiple
                                  className={styles.commentImagesInput}
                                  onChange={handleCurrentCommentImagesChange}
                                  aria-label="Add images, videos or audio for slideshow (max 10, 50MB each)"
                                />
                                Add Files
                              </label>
                              <span className={styles.commentImagesHint}>
                                Max {COMMENT_IMAGES_MAX} images/videos/audio, 50MB each. Only you can add files for your request.
                              </span>
                              {currentCommentImages.length > 0 && (
                                <>
                                  <span className={styles.commentImagesCount}>{currentCommentImages.length} file(s) selected</span>
                                  <button
                                    type="button"
                                    className={styles.commentImagesPreviewBtn}
                                    onClick={() => { setSlideshowPreviewIndex(0); setShowSlideshowPreview(true); }}
                                  >
                                    Preview
                                  </button>
                                  <div className={styles.commentImagesSelectedThumbs}>
                                    {currentCommentImagePreviewUrls.map((url, i) => {
                                      const file = currentCommentImages[i];
                                      const isVideo = file && file.type.startsWith('video/');
                                      const isAudio = file && file.type.startsWith('audio/');
                                      return (
                                        <span key={i} className={styles.commentImageThumbWrap}>
                                          {isVideo ? (
                                            <video src={url} className={styles.commentImageThumb} muted playsInline preload="metadata" />
                                          ) : isAudio ? (
                                            <span className={styles.commentImageThumbAudio}>Audio</span>
                                          ) : (
                                            <img src={url} alt="" className={styles.commentImageThumb} />
                                          )}
                                          <button
                                            type="button"
                                            className={styles.commentImageThumbRemove}
                                            onClick={() => handleRemoveCommentImage(i)}
                                            aria-label={`Remove file ${i + 1}`}
                                          >
                                            ×
                                          </button>
                                        </span>
                                      );
                                    })}
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                          {currentCommentImageError && (
                            <p className={styles.error} role="alert">{currentCommentImageError}</p>
                          )}
                        </>
                      );
                    })()}
                    {commentSentForReview && (
                      <p className={styles.commentSentForReview} role="status">
                        Comment sent for review. It will appear after the organizer approves.
                      </p>
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
            <p className={styles.muted}>No song is playing right now.</p>
          )}
        </div>
      )}
      {loadingRequests ? (
        <p className={styles.muted}>Loading...</p>
      ) : requests.length === 0 ? (
        <p className={styles.muted}>No requests yet. Click “Request a song” to add one.</p>
      ) : (
        <ul className={styles.requestList}>
          {requests.map((req, index) => {
            const isExpanded = expandedRequestId === req.id;
            const reqComments = comments[req.id] ?? [];
            const loadingCom = loadingComments[req.id];
            const commentInput = newCommentText[req.id] ?? '';
            const posting = postingComment[req.id];
            const requestAgainBusy = requestAgainLoading[req.id];

            return (
              <li key={req.id} className={styles.requestItem}>
                <div className={styles.requestSummaryRow}>
                  <button
                    type="button"
                    className={styles.requestSummary}
                    onClick={() => handleExpandRequest(req.id)}
                    aria-expanded={isExpanded}
                  >
                    <span className={styles.requestSerial}>{(page - 1) * REQUESTS_PAGE_LIMIT + index + 1}.</span>
                    <span className={styles.requestSong}>{req.songDescription || 'Song request'}</span>
                    <span className={styles.requestStatus} data-status={req.status}>
                      {req.status || '—'}
                    </span>
                    <span className={styles.requestExpand}>{isExpanded ? '▼' : '▶'}</span>
                  </button>
                  <button
                    type="button"
                    className={styles.requestAgainBtn}
                    onClick={(e) => { e.stopPropagation(); handleRequestAgain(req); }}
                    disabled={requestAgainBusy || !req.bookingId}
                    title={req.bookingId ? 'Create a new request with the same details' : 'Booking no longer available'}
                  >
                    {requestAgainBusy ? 'Requesting…' : 'Request again'}
                  </button>
                </div>
                {isExpanded && (
                  <div className={styles.requestDetail}>
                    {req.sourceLink && (
                      <p className={styles.requestMeta}>
                        Link: <a href={req.sourceLink} target="_blank" rel="noopener noreferrer">{req.sourceLink}</a>
                      </p>
                    )}
                    {req.customerComments && (
                      <p className={styles.requestMeta}>Dedication: {req.customerComments}</p>
                    )}
                    <div className={styles.commentsBlock}>
                      <h4 className={styles.commentsTitle}>Comments</h4>
                      {loadingCom ? (
                        <p className={styles.muted}>Loading comments...</p>
                      ) : (
                        <>
                          <ul className={styles.commentList}>
                            {reqComments.map((c) => (
                              <li key={c.id} className={styles.commentItem}>
                                <span className={styles.commentText}>{c.commentText}</span>
                                {c.createdAt && (
                                  <span className={styles.commentTime}>{new Date(c.createdAt).toLocaleString()}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {requests.length > 0 && (
        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>
            {total > 0
              ? `Page ${page} of ${Math.ceil(total / REQUESTS_PAGE_LIMIT) || 1} (${total} total)`
              : `Page ${page}`}
          </span>
          <div className={styles.paginationButtons}>
            <button
              type="button"
              className={styles.paginationBtn}
              disabled={page <= 1 || loadingRequests}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <button
              type="button"
              className={styles.paginationBtn}
              disabled={loadingRequests || (total > 0 && page >= Math.ceil(total / REQUESTS_PAGE_LIMIT)) || (total === 0 && requests.length < REQUESTS_PAGE_LIMIT)}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showSlideshowPreview && currentCommentImages.length > 0 && (
        <div
          className={styles.slideshowPreviewOverlay}
          role="dialog"
          aria-modal="true"
          aria-label="Preview slides"
          onClick={() => setShowSlideshowPreview(false)}
        >
          <div className={styles.slideshowPreviewBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.slideshowPreviewHeader}>
              <h3 className={styles.slideshowPreviewTitle}>Preview</h3>
              <button
                type="button"
                className={styles.slideshowPreviewClose}
                onClick={() => setShowSlideshowPreview(false)}
                aria-label="Close preview"
              >
                ×
              </button>
            </div>
            <p className={styles.slideshowPreviewCounter}>
              {slideshowPreviewIndex + 1} / {currentCommentImages.length}
            </p>
            <div className={styles.slideshowPreviewContent}>
              {(() => {
                const file = currentCommentImages[slideshowPreviewIndex];
                const url = currentCommentImagePreviewUrls[slideshowPreviewIndex];
                if (!file || !url) return null;
                const isVideo = file.type.startsWith('video/');
                const isAudio = file.type.startsWith('audio/');
                if (isVideo) {
                  return (
                    <video
                      key={slideshowPreviewIndex}
                      src={url}
                      className={styles.slideshowPreviewMedia}
                      controls
                      playsInline
                      preload="auto"
                    />
                  );
                }
                if (isAudio) {
                  return (
                    <div className={styles.slideshowPreviewAudioWrap}>
                      <p className={styles.slideshowPreviewAudioLabel}>Listen</p>
                      <audio key={slideshowPreviewIndex} src={url} className={styles.slideshowPreviewAudio} controls preload="metadata" />
                    </div>
                  );
                }
                return <img key={slideshowPreviewIndex} src={url} alt="" className={styles.slideshowPreviewMedia} />;
              })()}
            </div>
            <div className={styles.slideshowPreviewNav}>
              <button
                type="button"
                className={styles.slideshowPreviewNavBtn}
                disabled={slideshowPreviewIndex <= 0}
                onClick={() => setSlideshowPreviewIndex((i) => Math.max(0, i - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className={styles.slideshowPreviewNavBtn}
                disabled={slideshowPreviewIndex >= currentCommentImages.length - 1}
                onClick={() => setSlideshowPreviewIndex((i) => Math.min(currentCommentImages.length - 1, i + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
